import React, { useMemo } from "react";
import { useField } from "formik";
import { mapCategoryName, TransactionDirectionType } from "@common/categories";
import { Category } from "@common/types/category";
import { pluralize } from "@common/helpers/string";
import { TransferRuleDirection } from "@common/types/transactionRule";
import { CategoryIcon } from "@components/transactions/CategoryIcon";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { useCategories, CategoryStructure } from "@hooks/useCategories";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { HStack } from "@design-system/HStack/HStack";
import { TransactionRuleConditionFormSchema } from "../constants";
import { useEntityCategoryVisibility } from "../useEntityCategoryVisibility";
import { getFieldNamePrefix } from "../../utils";

interface Props {
    index: number;
    transferDirection?: TransferRuleDirection;
    controlButtonSize?: TriggerButtonProps["size"];
}

const CATEGORY_PLACEHOLDER = "Select category";

const toCategoryLabel = (category: Category) =>
    mapCategoryName({
        category,
        transactionDirection: TransactionDirectionType.mixed,
    });

export const CategoryConditionForm: React.FC<Props> = ({
    index,
    transferDirection,
    controlButtonSize,
}) => {
    const fieldName = `${getFieldNamePrefix(index, transferDirection)}.categoryIds`;
    const [field, , helpers] =
        useField<TransactionRuleConditionFormSchema["categoryIds"]>(fieldName);

    const { showBusiness, showPersonal } =
        useEntityCategoryVisibility(transferDirection);

    const parentCategories = useCategories({
        structure: CategoryStructure.TREE,
    });
    const categoriesById = useCategoriesById();

    const categoryItems = useMemo(() => {
        const isVisible = (category: Category) =>
            (showPersonal && !category.isBusiness) ||
            (showBusiness && category.isBusiness);
        return parentCategories
            .filter(isVisible)
            .map((parent): PickerItemData<string> => {
                const childItems = parent.subcategories
                    .filter(isVisible)
                    .map((child) => ({
                        value: child.id,
                        label: toCategoryLabel(child),
                    }));
                return {
                    value: parent.id,
                    label: toCategoryLabel(parent),
                    leading: <CategoryIcon categoryId={parent.id} />,
                    children: childItems.length > 0 ? childItems : undefined,
                };
            });
    }, [parentCategories, showBusiness, showPersonal]);

    const selectedCategoryIds = useMemo(() => field.value ?? [], [field.value]);

    const renderTriggerValue = (categoryIds: readonly string[]) => {
        const selectedCategories = categoryIds.flatMap((id) => {
            const category = categoriesById[id];
            return category ? [category] : [];
        });
        if (selectedCategories.length === 0) {
            return CATEGORY_PLACEHOLDER;
        }
        if (selectedCategories.length === 1) {
            return (
                <HStack as="span" gap={4} align="center">
                    <CategoryIcon categoryId={selectedCategories[0].id} />
                    {toCategoryLabel(selectedCategories[0])}
                </HStack>
            );
        }
        return `${selectedCategories.length} ${pluralize("category", selectedCategories.length)}`;
    };

    return (
        <Picker.Select
            items={categoryItems}
            multiple
            searchable
            defaultExpandAll
            value={selectedCategoryIds}
            onValueChange={(categoryIds) =>
                helpers.setValue(
                    categoryIds.length > 0 ? categoryIds : undefined,
                )
            }
            renderValue={renderTriggerValue}
            placeholder={CATEGORY_PLACEHOLDER}
            searchPlaceholder="Search categories..."
            emptyLabel="No matching categories"
            trigger={
                <Picker.Button
                    variant="primary"
                    size={controlButtonSize ?? "md"}
                />
            }
        />
    );
};
